/**
 * Shared client-side hardening for free-text inputs in the claims funnel.
 *
 * Strips control characters, bidirectional-override characters, and zero-width joiners; enforces
 * length caps. React text binding is XSS-safe for display — these helpers limit odd payloads,
 * UI-breaking characters, and spoofing in sessionStorage and POST bodies.
 */

/** Default narrative / describe textarea cap in the portal UI. */
export const CLAIM_NARRATIVE_MAX_LEN = 1000

/** API / DB narrative cap (server also truncates with ellipsis). */
export const CLAIM_API_NARRATIVE_MAX_LEN = 4000

/** Typical single-line address field bound (Entsia / JSON safe). */
export const INCIDENT_STREET_MAX_LEN = 255

/** Suburb / locality line bound. */
export const INCIDENT_SUBURB_MAX_LEN = 120

/** Matches the Location textarea cap on {@link ClaimIncidentStep}. */
export const INCIDENT_ROUGH_LOCATION_MAX_LEN = 500

/** Person name fields (driver, other party). */
export const CLAIM_PERSON_NAME_MAX_LEN = 120

/** Licence number field. */
export const CLAIM_LICENSE_NUMBER_MAX_LEN = 40

/** Phone / contact number field. */
export const CLAIM_PHONE_MAX_LEN = 40

/** Repairer / business name. */
export const CLAIM_REPAIRER_COMPANY_MAX_LEN = 200

/** Email contact field. */
export const CLAIM_EMAIL_MAX_LEN = 254

/** Single-vehicle collision “other” free text. */
export const CLAIM_COLLISION_OTHER_MAX_LEN = 500

/** Other-party address textarea. */
export const CLAIM_OTHER_PARTY_ADDRESS_MAX_LEN = 500

/** Windscreen attachment file name cap. */
export const CLAIM_ATTACHMENT_FILE_NAME_MAX_LEN = 240

const C0_CONTROL_EXCEPT_TAB = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g

/** Unicode bidirectional override characters (spoofing / UI confusion). */
const BIDI_OVERRIDE = /[\u202A-\u202E\u2066-\u2069]/g

/** Zero-width and format characters that can hide or reorder visible text. */
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF]/g

/** Angle brackets — reduce HTML/email header injection in downstream systems. */
const ANGLE_BRACKETS = /[<>]/g

export interface SanitizeClaimFreeTextOptions {
  maxLen: number
  /** When true, normalises CRLF to `\n`; when false, newlines become spaces. */
  multiline?: boolean
  /** When false, tabs are collapsed to spaces (default for single-line fields). */
  allowTab?: boolean
}

/**
 * Core sanitizer for claims free-text: control / bidi / zero-width removal, optional newline handling, length cap.
 */
export function sanitizeClaimFreeText(raw: string, options: SanitizeClaimFreeTextOptions): string {
  let s = raw.replace(C0_CONTROL_EXCEPT_TAB, '').replace(BIDI_OVERRIDE, '').replace(ZERO_WIDTH, '')
  if (options.allowTab === false) {
    s = s.replace(/\t/g, ' ')
  }
  if (options.multiline) {
    s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  } else {
    s = s.replace(/\r\n|\r|\n/g, ' ')
  }
  if (s.length > options.maxLen) {
    return s.slice(0, options.maxLen)
  }
  return s
}

/** Multiline describe / narrative fields (portal UI cap). */
export function sanitizeClaimMultilineNarrative(
  raw: string,
  maxLen: number = CLAIM_NARRATIVE_MAX_LEN
): string {
  return sanitizeClaimFreeText(raw, { maxLen, multiline: true, allowTab: true })
}

/** Multiline narrative for API submit bodies (higher cap). */
export function sanitizeClaimApiNarrative(raw: string): string {
  return sanitizeClaimMultilineNarrative(raw, CLAIM_API_NARRATIVE_MAX_LEN)
}

/** Generic single-line free-text field. */
export function sanitizeClaimSingleLineField(raw: string, maxLen: number): string {
  return sanitizeClaimFreeText(raw, { maxLen, multiline: false, allowTab: false })
}

/** Person / driver / other-party name. */
export function sanitizeClaimPersonName(raw: string, maxLen: number = CLAIM_PERSON_NAME_MAX_LEN): string {
  return sanitizeClaimSingleLineField(raw, maxLen)
}

/**
 * Licence number — alphanumeric, spaces, hyphens only (after control/bidi strip).
 */
export function sanitizeClaimLicenseNumber(
  raw: string,
  maxLen: number = CLAIM_LICENSE_NUMBER_MAX_LEN
): string {
  const base = sanitizeClaimSingleLineField(raw, maxLen)
  return base.replace(/[^A-Za-z0-9 \-]/g, '')
}

/** Phone contact — digits and common punctuation only. */
export function sanitizeClaimPhoneContact(raw: string, maxLen: number = CLAIM_PHONE_MAX_LEN): string {
  const base = sanitizeClaimSingleLineField(raw, maxLen)
  return base.replace(/[^0-9+().\- x]/gi, '')
}

/** Repairer / business company name. */
export function sanitizeClaimRepairerCompany(
  raw: string,
  maxLen: number = CLAIM_REPAIRER_COMPANY_MAX_LEN
): string {
  return sanitizeClaimSingleLineField(raw, maxLen)
}

/** Email — single line, no angle brackets, capped length. */
export function sanitizeClaimEmailContact(raw: string, maxLen: number = CLAIM_EMAIL_MAX_LEN): string {
  const base = sanitizeClaimSingleLineField(raw, maxLen)
  return base.replace(ANGLE_BRACKETS, '')
}

/** Attachment file name — no path separators or angle brackets. */
export function sanitizeClaimAttachmentFileName(
  raw: string,
  maxLen: number = CLAIM_ATTACHMENT_FILE_NAME_MAX_LEN
): string {
  const base = sanitizeClaimSingleLineField(raw, maxLen)
  return base.replace(/[/\\]/g, '').replace(ANGLE_BRACKETS, '')
}

/** Incident when/where single-line fields (street, suburb). */
export function sanitizeClaimIncidentSingleLine(raw: string, maxLen: number): string {
  return sanitizeClaimFreeText(raw, { maxLen, multiline: false, allowTab: true })
}

/** Incident rough location textarea. */
export function sanitizeClaimIncidentRoughLocation(raw: string): string {
  return sanitizeClaimFreeText(raw, {
    maxLen: INCIDENT_ROUGH_LOCATION_MAX_LEN,
    multiline: true,
    allowTab: true,
  })
}
